import fs from 'node:fs';
import path from 'node:path';

// Import all module functions
import {
  // Constants
  HEADER_COMMENT,
  CUSTOM_TYPE_PATTERN,
  // Header & Format
  ensureHeaderComment,
  stripLeadingNewlines,
  collapseExtraBlankLines,
  // Schema Conversion
  removePgSchemaDeclarations,
  convertSchemaTableInvocations,
  // Table Rename
  renamePgTableConstants,
  updateTableReferenceIdentifiers,
  escapeRegExp,
  sanitizeIdentifier,
  toAsciiName,
  // Custom Types
  replaceUnknownColumns,
  replaceFollowingUnknown,
  // Imports
  tweakImports,
  ensureTypesImport,
  removeInlineTypeHelpers,
  inlineCustomTypes,
  inlineFromTemplate,
  ensureImportIdentifier,
  // System Fields
  addSystemFieldComments,
  removeConflictingSystemFields,
  // Patch
  patchDrizzleKitDefects,
  // Timestamp Replacement
  replaceTimestampWithCustomTypes,
  replaceDefaultNowWithSql,
  // Table Aliases
  appendTableAliases,
} from './helper';

type PostprocessStats = {
  replacedUnknown: number;
  unmatchedUnknown: string[];
  patchedDefects: number;
  replacedTimestamps: number;
  replacedDefaultNow: number;
};

// Internal functions (exported for testing)
export const internal = {
  CUSTOM_TYPE_PATTERN,
  HEADER_COMMENT,
  ensureHeaderComment,
  stripLeadingNewlines,
  collapseExtraBlankLines,
  removePgSchemaDeclarations,
  convertSchemaTableInvocations,
  sanitizeIdentifier,
  toAsciiName,
  renamePgTableConstants,
  updateTableReferenceIdentifiers,
  escapeRegExp,
  replaceUnknownColumns,
  replaceFollowingUnknown,
  addSystemFieldComments,
  tweakImports,
  ensureTypesImport,
  removeInlineTypeHelpers,
  inlineCustomTypes,
  inlineFromTemplate,
  ensureImportIdentifier,
  removeConflictingSystemFields,
  patchDrizzleKitDefects,
  replaceTimestampWithCustomTypes,
  replaceDefaultNowWithSql,
  appendTableAliases,
};

export function postprocessDrizzleSchema(targetPath: string): PostprocessStats | undefined {
  const resolvedPath = path.resolve(targetPath);
  if (!fs.existsSync(resolvedPath)) {
    console.warn(`[postprocess-drizzle-schema] File not found: ${resolvedPath}`);
    return undefined;
  }

  let text = fs.readFileSync(resolvedPath, 'utf8');

  // Processing pipeline - maintain exact order
  text = ensureHeaderComment(text);

  // Patch drizzle-kit defects first
  const patchResult = patchDrizzleKitDefects(text);
  text = patchResult.text;

  text = removePgSchemaDeclarations(text);
  const tableConversion = convertSchemaTableInvocations(text);
  text = tableConversion.text;
  const renameResult = renamePgTableConstants(text);
  text = renameResult.text;
  text = updateTableReferenceIdentifiers(text, renameResult.renames);

  const replacement = replaceUnknownColumns(text);
  text = replacement.text;

  // Replace timestamp with specific options to custom types
  const timestampReplacement = replaceTimestampWithCustomTypes(text);
  text = timestampReplacement.text;

  // Replace .defaultNow() with .default(sql`CURRENT_TIMESTAMP`)
  const defaultNowReplacement = replaceDefaultNowWithSql(text);
  text = defaultNowReplacement.text;

  // Remove conflicting system fields before adding comments
  text = removeConflictingSystemFields(text);

  text = addSystemFieldComments(text);

  text = tweakImports(text);
  // Inline custom types instead of importing from external file
  text = inlineCustomTypes(text);

  text = appendTableAliases(text);
  // Note: removeInlineTypeHelpers is no longer needed since we intentionally inline types now
  text = text.replace(/\r?\n/g, '\n');
  text = collapseExtraBlankLines(text);

  fs.writeFileSync(resolvedPath, text, 'utf8');

  if (patchResult.fixed > 0) {
    console.info(`[postprocess-drizzle-schema] Patched ${patchResult.fixed} drizzle-kit defects (.default(') -> .default(''))`);
  }
  if (replacement.replaced > 0) {
    console.info(`[postprocess-drizzle-schema] Replaced ${replacement.replaced} unknown columns`);
  }
  if (replacement.unmatched.length > 0) {
    console.warn('[postprocess-drizzle-schema] Unmatched custom types:', replacement.unmatched.length);
    replacement.unmatched.forEach((line) => console.warn(`  ${line}`));
  }
  if (tableConversion.converted > 0) {
    console.info(`[postprocess-drizzle-schema] Converted ${tableConversion.converted} schema.table invocations to pgTable`);
  }
  if (timestampReplacement.replaced > 0) {
    console.info(`[postprocess-drizzle-schema] Replaced ${timestampReplacement.replaced} timestamp fields with customTimestamptz`);
  }
  if (defaultNowReplacement.replaced > 0) {
    console.info(`[postprocess-drizzle-schema] Replaced ${defaultNowReplacement.replaced} .defaultNow() with .default(sql\`CURRENT_TIMESTAMP\`)`);
  }

  return {
    replacedUnknown: replacement.replaced,
    unmatchedUnknown: replacement.unmatched,
    patchedDefects: patchResult.fixed,
    replacedTimestamps: timestampReplacement.replaced,
    replacedDefaultNow: defaultNowReplacement.replaced,
  };
}
